import React, { useState, useEffect, useMemo } from 'react'
import { TimeframeSelector } from './TimeframeSelector'
import { useConversionFunnel } from '@/hooks/useConversionFunnel'
import { Users, Building2, Globe, Calendar, Mail, UserCheck, ArrowLeft, Download, Settings, Reply, MessageSquare, Filter, TrendingUp, BarChart3, Plus, Edit, Trash2, Target, Crown, Database, Eye } from 'lucide-react'
import { ManualMeetingDialog } from './ManualMeetingDialog'
import { MeetingDetailView } from './MeetingDetailView'
import { supabase } from '@/integrations/supabase/client'
import { format, subDays } from 'date-fns'
import { utcStartOfMonth, utcStartOfDay, utcEndOfDay, utcStartOfYear, utcStartOfPrevMonth, utcEndOfPrevMonth } from '@/lib/utc-date-utils'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { EditableDropdown } from '@/components/ui/editable-dropdown'
import { InlineEdit } from '@/components/ui/inline-edit'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts'
import { DataTableColumnConfig } from './DataTableColumnConfig'
import { useClients } from '@/hooks/useClients'

interface NewMeetingsBookedDetailViewProps {
  onBack: () => void
  clientFilter?: string
  timeFrame?: string
  customDateRange?: {
    from: Date
    to: Date
  }
  onTimeFrameChange?: (timeFrame: string) => void
  onCustomDateRangeChange?: (dateRange: { from: Date; to: Date } | undefined) => void
}

interface ColumnConfig {
  id: string
  label: string
  visible: boolean
  order: number
}

interface MeetingRecord {
  id: string
  meetings_booked_create_date: string
  client: string
  client_id: number
  lead_email: string
  email_domain: string
  campaign_name: string
  source: string
  attribution_days: number
  direct_meeting: boolean
  email_sent_time: string
  op_created_date: string
  sf_account_id: string
  lead_status?: string
  meeting_status: 'Meeting Booked' | 'Qualified Meeting' // New field to distinguish type
  table_source: 'meetings_booked' | 'qualified_meetings' // Track which table the data came from
  meeting_tier?: string // Add the missing meeting_tier field
  is_meeting_booked?: boolean
  meeting_booked_date?: string
  is_qualified_meeting?: boolean
  qualified_meeting_date?: string
  created_at: string
  updated_at: string
}

export const NewMeetingsBookedDetailView = ({ 
  onBack, 
  clientFilter, 
  timeFrame = 'MTD', 
  customDateRange,
  onTimeFrameChange,
  onCustomDateRangeChange
}: NewMeetingsBookedDetailViewProps) => {
  const [rawMeetings, setRawMeetings] = useState<MeetingRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [sourceFilter, setSourceFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('all') // New filter for meeting status
  const [monthFilter, setMonthFilter] = useState<string>('all') // New filter for meeting month
  const [tierFilter, setTierFilter] = useState<string>('all')
  const [emailDomainFilter, setEmailDomainFilter] = useState<string>('')
  const [campaignNameFilter, setCampaignNameFilter] = useState<string>('')
  
  // Manual meeting management state
  const [isAddMeetingOpen, setIsAddMeetingOpen] = useState(false)
  const [isEditMeetingOpen, setIsEditMeetingOpen] = useState(false)
  const [isDetailViewOpen, setIsDetailViewOpen] = useState(false)
  const [isBulkEditOpen, setIsBulkEditOpen] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState<MeetingRecord | null>(null)
  
  // Bulk selection state
  const [selectedMeetings, setSelectedMeetings] = useState<Set<string>>(new Set())
  const [isAllSelected, setIsAllSelected] = useState(false)
  
  // Column configuration state
  const [columnConfigs, setColumnConfigs] = useState<ColumnConfig[]>([
    { id: 'meetings_booked_create_date', label: 'Meeting Date', visible: true, order: 0 },
    { id: 'meeting_month', label: 'Meeting Month', visible: true, order: 1 },
    { id: 'meeting_status', label: 'Status', visible: true, order: 2 },
    { id: 'is_meeting_booked', label: 'Meeting Booked', visible: true, order: 3 },
    { id: 'is_qualified_meeting', label: 'Qualified Meeting', visible: true, order: 4 },
    { id: 'client', label: 'Client', visible: true, order: 5 },
    { id: 'lead_email', label: 'Lead Email', visible: true, order: 6 },
    { id: 'email_domain', label: 'Domain', visible: true, order: 7 },
    { id: 'campaign_name', label: 'Campaign', visible: true, order: 8 },
    { id: 'meeting_tier', label: 'Meeting Tier', visible: true, order: 9 },
    { id: 'source', label: 'Source', visible: true, order: 10 },
    { id: 'direct_meeting', label: 'Direct Meeting', visible: true, order: 11 },
    { id: 'attribution_days', label: 'Attribution Days', visible: true, order: 12 },
    { id: 'lead_status', label: 'Lead Status', visible: true, order: 13 },
    { id: 'email_sent_time', label: 'Email Sent', visible: false, order: 14 },
    { id: 'op_created_date', label: 'Opportunity Created', visible: false, order: 15 },
    { id: 'sf_account_id', label: 'Salesforce ID', visible: false, order: 16 }
  ])

  const { data: conversionData } = useConversionFunnel({ 
    timeFrame,
    clientId: clientFilter,
    dateRange: customDateRange
  })

  const { data: clients = [] } = useClients()

  const formatNumber = (num: number) => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M'
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'k'
    }
    return num.toString()
  }

  // Get date range for the selected timeframe
  // All boundaries use UTC to match Postgres timestamps regardless of browser timezone.
  const getDateRange = () => {
    const now = new Date()
    let endDate = new Date(now)
    let startDate = new Date(now)

    switch (timeFrame) {
      case 'MTD':
        startDate = utcStartOfMonth(now)
        endDate = now
        break
      case 'LAST_7_DAYS':
        startDate = subDays(now, 7)
        break
      case 'LAST_30_DAYS':
        startDate = subDays(now, 30)
        break
      case 'LAST_90_DAYS':
        startDate = subDays(now, 90)
        break
      case 'LAST_MONTH':
        startDate = utcStartOfPrevMonth(now)
        endDate = utcEndOfPrevMonth(now)
        break
      case 'YTD':
        startDate = utcStartOfYear(now)
        break
      case 'CUSTOM':
        if (customDateRange?.from && customDateRange?.to) {
          startDate = utcStartOfDay(customDateRange.from)
          endDate = utcEndOfDay(customDateRange.to)
        } else {
          startDate = subDays(now, 30)
        }
        break
      default:
        startDate = subDays(now, 30)
    }

    return { startDate, endDate }
  }

  // Fetch unified meetings data from both tables
  useEffect(() => {
    const fetchUnifiedMeetings = async () => {
      try {
        setLoading(true)
        const { startDate, endDate } = getDateRange()
        
        console.log('Fetching unified meetings with date range:', {
          start: startDate.toISOString(),
          end: endDate.toISOString(),
          client: clientFilter,
          timeFrame
        })
        
        // Fetch meetings booked
        let meetingsQuery = supabase.from('meetings_booked').select('*')
        
        // Fetch qualified meetings  
        let qualifiedQuery = supabase.from('qualified_meetings').select('*')
        
        // Apply client filtering to both queries
        if (clientFilter && clientFilter !== 'all') {
          meetingsQuery = meetingsQuery.eq('client_id', clientFilter)
          qualifiedQuery = qualifiedQuery.eq('client_id', clientFilter)
        }
        
        // Apply date filtering to both queries
        meetingsQuery = meetingsQuery
          .gte('meetings_booked_create_date', startDate.toISOString())
          .lte('meetings_booked_create_date', endDate.toISOString())
          .order('meetings_booked_create_date', { ascending: false })
          .limit(250)
          
        qualifiedQuery = qualifiedQuery
          .gte('qualified_meetings_create_date', startDate.toISOString())
          .lte('qualified_meetings_create_date', endDate.toISOString())
          .order('qualified_meetings_create_date', { ascending: false })
          .limit(250)
        
        const [meetingsResult, qualifiedResult] = await Promise.all([
          meetingsQuery,
          qualifiedQuery
        ])
        
        if (meetingsResult.error || qualifiedResult.error) {
          console.error('Error fetching meetings:', {
            meetingsError: meetingsResult.error,
            qualifiedError: qualifiedResult.error
          })
          return
        }
        
        // Transform and combine the data
        const meetingsData: MeetingRecord[] = (meetingsResult.data || []).map(meeting => ({
          ...meeting,
          meeting_status: 'Meeting Booked' as const,
          table_source: 'meetings_booked' as const
        }))
        
        const qualifiedData: MeetingRecord[] = (qualifiedResult.data || []).map(qualified => ({
          ...qualified,
          meetings_booked_create_date: qualified.qualified_meetings_create_date, // Map the date field
          meeting_status: 'Qualified Meeting' as const,
          table_source: 'qualified_meetings' as const
        }))
        
        // Consolidate meetings: if same domain + date + client exists in both tables, show only qualified
        const consolidatedData = new Map<string, MeetingRecord>()
        
        // First add all meetings booked
        meetingsData.forEach(meeting => {
          const key = `${meeting.email_domain}-${format(new Date(meeting.meetings_booked_create_date), 'yyyy-MM-dd')}-${meeting.client_id}`
          consolidatedData.set(key, meeting)
        })
        
        // Then override with qualified meetings (they take priority)
        qualifiedData.forEach(qualified => {
          const key = `${qualified.email_domain}-${format(new Date(qualified.meetings_booked_create_date), 'yyyy-MM-dd')}-${qualified.client_id}`
          consolidatedData.set(key, qualified)
        })
        
        // Convert back to array and sort by date
        const combinedData = Array.from(consolidatedData.values()).sort((a, b) => 
          new Date(b.meetings_booked_create_date).getTime() - new Date(a.meetings_booked_create_date).getTime()
        )
        
        console.log('Unified meetings fetched:', {
          meetings: meetingsData.length,
          qualified: qualifiedData.length,
          total: combinedData.length
        })
        
        setRawMeetings(combinedData)
      } catch (error) {
        console.error('Error in fetchUnifiedMeetings:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchUnifiedMeetings()
  }, [timeFrame, customDateRange, clientFilter])

  // Helper function to update meeting field
  const updateMeetingField = async (meetingId: string, tableName: 'meetings_booked' | 'qualified_meetings', field: string, value: any) => {
    try {
      const { error } = await supabase
        .from(tableName)
        .update({ [field]: value })
        .eq('id', meetingId)
      
      if (error) {
        console.error(`Error updating ${field}:`, error)
        return false
      }
      
      return true
    } catch (err) {
      console.error(`Exception updating ${field}:`, err)
      return false
    }
  }

  // Enhanced filtering logic
  const filteredMeetings = useMemo(() => {
    return rawMeetings.filter(meeting => {
      // Source filter
      if (sourceFilter !== 'all') {
        if (sourceFilter === 'breakcold' && meeting.source !== 'breakcold') return false
        if (sourceFilter === 'sqls_meetings_booked' && meeting.source !== 'sqls_meetings_booked') return false
        if (sourceFilter === 'merged' && meeting.source !== 'merged') return false
      }
      
      // Status filter
      if (statusFilter !== 'all') {
        if (statusFilter === 'meeting_booked' && meeting.meeting_status !== 'Meeting Booked') return false
        if (statusFilter === 'qualified' && meeting.meeting_status !== 'Qualified Meeting') return false
      }
      
      // Month filter
      if (monthFilter !== 'all') {
        if (meeting.meetings_booked_create_date) {
          const meetingMonth = format(new Date(meeting.meetings_booked_create_date), 'yyyy-MM')
          if (monthFilter !== meetingMonth) return false
        }
      }
      
      // Tier filter (for meeting tier)
      if (tierFilter !== 'all') {
        if (tierFilter === 'Tier 1' || tierFilter === 'Tier 2' || tierFilter === 'Tier 3' || tierFilter === 'Tier 4' || tierFilter === 'Individual') {
          const meetingTier = meeting.meeting_tier || 'Tier 1'
          if (tierFilter !== meetingTier) return false
        }
      }
      
      // Email domain filter
      if (emailDomainFilter && !meeting.email_domain?.toLowerCase().includes(emailDomainFilter.toLowerCase())) {
        return false
      }
      
      // Campaign name filter
      if (campaignNameFilter && !meeting.campaign_name?.toLowerCase().includes(campaignNameFilter.toLowerCase())) {
        return false
      }
      
      return true
    })
  }, [rawMeetings, sourceFilter, statusFilter, monthFilter, tierFilter, emailDomainFilter, campaignNameFilter])

  // Calculate daily breakdown for chart
  const dailyBreakdown = useMemo(() => {
    const breakdown = new Map<string, number>()
    
    filteredMeetings.forEach(meeting => {
      const date = meeting.meetings_booked_create_date ? format(new Date(meeting.meetings_booked_create_date), 'MMM dd') : 'Unknown'
      breakdown.set(date, (breakdown.get(date) || 0) + 1)
    })
    
    return Array.from(breakdown.entries())
      .map(([date, count]) => ({ date, count }))
      .sort((a, b) => new Date(a.date + ' 2024').getTime() - new Date(b.date + ' 2024').getTime())
  }, [filteredMeetings])

  // Source breakdown for pie chart
  const sourceBreakdown = useMemo(() => {
    const breakdown = new Map<string, number>()
    
    filteredMeetings.forEach(meeting => {
      const source = meeting.source || 'Unknown'
      breakdown.set(source, (breakdown.get(source) || 0) + 1)
    })
    
    const colors = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6']
    
    return Array.from(breakdown.entries()).map(([name, value], index) => ({
      name: name === 'breakcold' ? 'Direct' : name === 'sqls_meetings_booked' ? 'Attribution' : name,
      value,
      fill: colors[index % colors.length]
    }))
  }, [filteredMeetings])

  // Direct vs Attribution breakdown for pie chart
  const directAttributionBreakdown = useMemo(() => {
    const breakdown = new Map<string, number>()
    
    filteredMeetings.forEach(meeting => {
      const type = meeting.direct_meeting ? 'Direct' : 'Attribution'
      breakdown.set(type, (breakdown.get(type) || 0) + 1)
    })
    
    const colors = ['#22c55e', '#3b82f6']
    
    return Array.from(breakdown.entries()).map(([name, value], index) => ({
      name,
      value,
      fill: colors[index % colors.length]
    }))
  }, [filteredMeetings])

  // Export to CSV function
  const exportToCsv = () => {
    const headers = columnConfigs
      .filter(col => col.visible)
      .sort((a, b) => a.order - b.order)
      .map(col => col.label)
    
    const csvContent = [
      headers.join(','),
      ...filteredMeetings.map(meeting => 
        columnConfigs
          .filter(col => col.visible)
          .sort((a, b) => a.order - b.order)
          .map(col => {
            const value = meeting[col.id as keyof MeetingRecord]
            if (col.id === 'meetings_booked_create_date' && value) {
              return `"${format(new Date(value as string), 'MMM dd, yyyy HH:mm')}"`
            }
            if (col.id === 'direct_meeting') {
              return value ? 'Yes' : 'No'
            }
            return `"${value || ''}"`
          })
          .join(',')
      )
    ].join('\n')
    
    const blob = new Blob([csvContent], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `meetings-booked-${format(new Date(), 'yyyy-MM-dd')}.csv`
    a.click()
    window.URL.revokeObjectURL(url)
  }

  // Column configuration handlers
  const handleColumnToggle = (columnId: string) => {
    setColumnConfigs(prev => 
      prev.map(col => 
        col.id === columnId ? { ...col, visible: !col.visible } : col
      )
    )
  }

  const handleColumnReorder = (dragIndex: number, hoverIndex: number) => {
    setColumnConfigs(prev => {
      const newConfigs = [...prev].sort((a, b) => a.order - b.order)
      const draggedItem = newConfigs[dragIndex]
      newConfigs.splice(dragIndex, 1)
      newConfigs.splice(hoverIndex, 0, draggedItem)
      
      // Update order values
      return newConfigs.map((item, index) => ({ ...item, order: index }))
    })
  }

  // Manual meeting management handlers
  const handleAddMeeting = () => {
    setSelectedMeeting(null)
    setIsAddMeetingOpen(true)
  }

  const handleEditMeeting = (meeting: MeetingRecord) => {
    setSelectedMeeting(meeting)
    setIsEditMeetingOpen(true)
  }

  const handleViewMeeting = (meeting: MeetingRecord) => {
    console.log('Opening meeting detail for:', meeting.lead_email)
    setSelectedMeeting(meeting)
    setIsDetailViewOpen(true)
  }

  const handleDeleteMeeting = async () => {
    if (!selectedMeeting) return
    
    try {
      const table = selectedMeeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
      
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('id', selectedMeeting.id)
      
      if (error) throw error
      
      // Refresh the unified data
      fetchUnifiedMeetings()
      setIsDetailViewOpen(false)
      setSelectedMeeting(null)
    } catch (error) {
      console.error('Error deleting meeting:', error)
    }
  }

  const fetchUnifiedMeetings = async () => {
    try {
      setLoading(true)
      const { startDate, endDate } = getDateRange()
      
      // Fetch meetings booked
      let meetingsQuery = supabase.from('meetings_booked').select('*')
      
      // Fetch qualified meetings  
      let qualifiedQuery = supabase.from('qualified_meetings').select('*')
      
      // Apply client filtering to both queries
      if (clientFilter && clientFilter !== 'all') {
        meetingsQuery = meetingsQuery.eq('client_id', clientFilter)
        qualifiedQuery = qualifiedQuery.eq('client_id', clientFilter)
      }
      
      // Apply date filtering to both queries
      meetingsQuery = meetingsQuery
        .gte('meetings_booked_create_date', startDate.toISOString())
        .lte('meetings_booked_create_date', endDate.toISOString())
        .order('meetings_booked_create_date', { ascending: false })
        .limit(250)
        
      qualifiedQuery = qualifiedQuery
        .gte('qualified_meetings_create_date', startDate.toISOString())
        .lte('qualified_meetings_create_date', endDate.toISOString())
        .order('qualified_meetings_create_date', { ascending: false })
        .limit(250)
      
      const [meetingsResult, qualifiedResult] = await Promise.all([
        meetingsQuery,
        qualifiedQuery
      ])
      
      if (meetingsResult.error || qualifiedResult.error) {
        console.error('Error fetching meetings:', {
          meetingsError: meetingsResult.error,
          qualifiedError: qualifiedResult.error
        })
        return
      }
      
      // Transform and combine the data
      const meetingsData: MeetingRecord[] = (meetingsResult.data || []).map(meeting => ({
        ...meeting,
        meeting_status: 'Meeting Booked' as const,
        table_source: 'meetings_booked' as const
      }))
      
      const qualifiedData: MeetingRecord[] = (qualifiedResult.data || []).map(qualified => ({
        ...qualified,
        meetings_booked_create_date: qualified.qualified_meetings_create_date, // Map the date field
        meeting_status: 'Qualified Meeting' as const,
        table_source: 'qualified_meetings' as const
      }))
      
      // Consolidate meetings: if same domain + date + client exists in both tables, show only qualified
      const consolidatedData = new Map<string, MeetingRecord>()
      
      // First add all meetings booked
      meetingsData.forEach(meeting => {
        const key = `${meeting.email_domain}-${format(new Date(meeting.meetings_booked_create_date), 'yyyy-MM-dd')}-${meeting.client_id}`
        consolidatedData.set(key, meeting)
      })
      
      // Then override with qualified meetings (they take priority)
      qualifiedData.forEach(qualified => {
        const key = `${qualified.email_domain}-${format(new Date(qualified.meetings_booked_create_date), 'yyyy-MM-dd')}-${qualified.client_id}`
        consolidatedData.set(key, qualified)
      })
      
      // Convert back to array and sort by date
      const combinedData = Array.from(consolidatedData.values()).sort((a, b) => 
        new Date(b.meetings_booked_create_date).getTime() - new Date(a.meetings_booked_create_date).getTime()
      )
      
      console.log('Unified meetings fetched:', {
        meetings: meetingsData.length,
        qualified: qualifiedData.length,
        total: combinedData.length,
        consolidated: consolidatedData.size
      })
      
      setRawMeetings(combinedData)
    } catch (error) {
      console.error('Error in fetchUnifiedMeetings:', error)
    } finally {
      setLoading(false)
    }
  }

  const handlePromoteToQualified = async (meeting: MeetingRecord) => {
    try {
      // Create a new record in qualified_meetings table
      const qualifiedMeeting = {
        client: meeting.client,
        client_id: meeting.client_id,
        lead_email: meeting.lead_email,
        email_domain: meeting.email_domain,
        campaign_name: meeting.campaign_name,
        source: meeting.source,
        attribution_days: meeting.attribution_days,
        direct_meeting: meeting.direct_meeting,
        email_sent_time: meeting.email_sent_time,
        op_created_date: meeting.op_created_date,
        qualified_meetings_create_date: new Date().toISOString(), // Mark as qualified now
        lead_status: 'Qualified Meeting',
        sf_account_id: meeting.sf_account_id
      }
      
      const { error: insertError } = await supabase
        .from('qualified_meetings')
        .insert([qualifiedMeeting])
      
      if (insertError) throw insertError
      
      // Remove from meetings_booked table
      const { error: deleteError } = await supabase
        .from('meetings_booked')
        .delete()
        .eq('id', meeting.id)
      
      if (deleteError) throw deleteError
      
      // Refresh the unified data
      fetchUnifiedMeetings()
    } catch (error) {
      console.error('Error promoting meeting to qualified:', error)
    }
  }

  const handleMeetingSaved = async () => {
    // Refresh the unified data after adding/editing
    fetchUnifiedMeetings()
  }

  // Bulk selection handlers
  const handleSelectMeeting = (meetingId: string, checked: boolean) => {
    const newSelected = new Set(selectedMeetings)
    if (checked) {
      newSelected.add(meetingId)
    } else {
      newSelected.delete(meetingId)
      setIsAllSelected(false)
    }
    setSelectedMeetings(newSelected)
  }

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = new Set(filteredMeetings.map(m => m.id))
      setSelectedMeetings(allIds)
      setIsAllSelected(true)
    } else {
      setSelectedMeetings(new Set())
      setIsAllSelected(false)
    }
  }

  const handleBulkDelete = async () => {
    if (selectedMeetings.size === 0) return
    
    try {
      const deletePromises = Array.from(selectedMeetings).map(async (meetingId) => {
        const meeting = filteredMeetings.find(m => m.id === meetingId)
        if (!meeting) return
        
        const table = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
        return supabase.from(table).delete().eq('id', meetingId)
      })
      
      await Promise.all(deletePromises)
      
      // Clear selections and refresh data
      setSelectedMeetings(new Set())
      setIsAllSelected(false)
      fetchUnifiedMeetings()
    } catch (error) {
      console.error('Error bulk deleting meetings:', error)
    }
  }

  const handleBulkPromoteToQualified = async () => {
    if (selectedMeetings.size === 0) return
    
    try {
      const promotePromises = Array.from(selectedMeetings).map(async (meetingId) => {
        const meeting = filteredMeetings.find(m => m.id === meetingId)
        if (!meeting || meeting.meeting_status === 'Qualified Meeting') return
        
        // Insert into qualified_meetings
        const { error: insertError } = await supabase
          .from('qualified_meetings')
          .insert({
            client: meeting.client,
            client_id: meeting.client_id,
            lead_email: meeting.lead_email,
            email_domain: meeting.email_domain,
            email_sent_time: meeting.email_sent_time,
            op_created_date: meeting.op_created_date,
            qualified_meetings_create_date: meeting.meetings_booked_create_date,
            direct_meeting: meeting.direct_meeting,
            lead_status: 'Qualified Meeting',
            attribution_days: meeting.attribution_days,
            source: meeting.source,
            campaign_name: meeting.campaign_name,
            sf_account_id: meeting.sf_account_id
          })
        
        if (insertError) throw insertError
        
        // Delete from meetings_booked
        const { error: deleteError } = await supabase
          .from('meetings_booked')
          .delete()
          .eq('id', meeting.id)
        
        if (deleteError) throw deleteError
      })
      
      await Promise.all(promotePromises)
      
      // Clear selections and refresh data
      setSelectedMeetings(new Set())
      setIsAllSelected(false)
      fetchUnifiedMeetings()
    } catch (error) {
      console.error('Error bulk promoting meetings:', error)
    }
  }

  // Ensure page starts at the top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Meetings Management</h1>
            <p className="text-muted-foreground">
              Unified view of meetings booked and qualified meetings from outreach campaigns
            </p>
          </div>
        </div>
        
        {onTimeFrameChange && onCustomDateRangeChange && (
          <TimeframeSelector
            timeFrame={timeFrame}
            customDateRange={customDateRange ? { from: customDateRange.from, to: customDateRange.to } : undefined}
            onTimeFrameChange={onTimeFrameChange}
            onCustomDateRangeChange={onCustomDateRangeChange}
          />
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Meetings</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{filteredMeetings.length}</div>
            <p className="text-xs text-muted-foreground">
              All meetings this period
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow border-blue-200 bg-blue-50"
          onClick={() => {
            const element = document.querySelector('[data-section="meetings-booked"]');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-blue-800">Meetings Booked</CardTitle>
            <Calendar className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-blue-600">
              {filteredMeetings.filter(m => m.meeting_status === 'Meeting Booked').length}
            </div>
            <p className="text-xs text-blue-600">
              Initial meetings scheduled
            </p>
          </CardContent>
        </Card>

        <Card 
          className="cursor-pointer hover:shadow-md transition-shadow border-emerald-200 bg-emerald-50"
          onClick={() => {
            const element = document.querySelector('[data-section="qualified-meetings"]');
            if (element) {
              element.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }
          }}
        >
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-emerald-800">Qualified Meetings</CardTitle>
            <UserCheck className="h-4 w-4 text-emerald-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-emerald-700">
              {filteredMeetings.filter(m => m.meeting_status === 'Qualified Meeting').length}
            </div>
            <p className="text-xs text-emerald-600">
              Meetings that passed qualification
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Qualification Rate</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {filteredMeetings.length > 0 
                ? Math.round((filteredMeetings.filter(m => m.meeting_status === 'Qualified Meeting').length / filteredMeetings.length) * 100)
                : 0}%
            </div>
            <p className="text-xs text-muted-foreground">
              Conversion to qualified
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Avg per Day</CardTitle>
            <BarChart3 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {dailyBreakdown.length > 0 ? Math.round(filteredMeetings.length / dailyBreakdown.length) : 0}
            </div>
            <p className="text-xs text-muted-foreground">
              Meetings per day this period
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Daily Meetings Chart */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Meetings Booked Over Time
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" style={{ minHeight: '320px' }}>
              {dailyBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%" key="daily-chart">
                  <BarChart data={dailyBreakdown}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#3b82f6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No data to display
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Source Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Source Breakdown
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" style={{ minHeight: '320px' }}>
              {sourceBreakdown.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%" key="source-chart">
                  <PieChart>
                    <Pie
                      data={sourceBreakdown}
                      cx="50%"
                      cy="50%"
                      outerRadius={80}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                      animationBegin={0}
                      animationDuration={0}
                    >
                      {sourceBreakdown.map((entry, index) => (
                        <Cell key={`source-cell-${index}`} fill={entry.fill} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground">
                  No data to display
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Direct vs Attribution Breakdown Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Direct vs Attribution Meetings
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-80" style={{ minHeight: '320px' }}>
            {directAttributionBreakdown.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%" key="attribution-chart">
                <BarChart data={directAttributionBreakdown}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="value" fill="#22c55e" />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                No data to display
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Raw Meetings Data Section */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Meetings Management
              <div className="flex items-center gap-2 ml-4">
                <div className="flex items-center gap-1 text-sm text-muted-foreground">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                  Booked ({filteredMeetings.filter(m => m.meeting_status === 'Meeting Booked').length})
                </div>
                <div className="flex items-center gap-1 text-sm text-emerald-600 font-medium">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  Qualified ({filteredMeetings.filter(m => m.meeting_status === 'Qualified Meeting').length})
                </div>
              </div>
            </CardTitle>
            <div className="flex items-center gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleAddMeeting}
                className="gap-2"
              >
                <Plus className="w-4 h-4" />
                Add Meeting
              </Button>
              <DataTableColumnConfig
                columns={columnConfigs}
                onColumnToggle={handleColumnToggle}
                onColumnReorder={handleColumnReorder}
              />
              <Button
                variant="outline"
                size="sm"
                onClick={exportToCsv}
                className="gap-2"
                disabled={filteredMeetings.length === 0}
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-4">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="breakcold">Direct</SelectItem>
                <SelectItem value="sqls_meetings_booked">Attribution</SelectItem>
                <SelectItem value="merged">Merged</SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-48 border-emerald-200">
                <SelectValue placeholder="Meeting Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="meeting_booked">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    Meeting Booked
                  </div>
                </SelectItem>
                <SelectItem value="qualified">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    Qualified Meeting
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={tierFilter} onValueChange={setTierFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Tier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tiers</SelectItem>
                <SelectItem value="Standard">Standard</SelectItem>
                <SelectItem value="Premium">Premium</SelectItem>
                <SelectItem value="Enterprise">Enterprise</SelectItem>
              </SelectContent>
            </Select>
            <Select value={monthFilter} onValueChange={setMonthFilter}>
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Months</SelectItem>
                {Array.from(new Set(rawMeetings.map(m => 
                  m.meetings_booked_create_date ? format(new Date(m.meetings_booked_create_date), 'yyyy-MM') : null
                ).filter(Boolean))).sort().reverse().map(month => (
                  <SelectItem key={month} value={month || ''}>
                    {month ? format(new Date(month + '-01'), 'MMM yyyy') : 'Unknown'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              placeholder="Filter by domain..."
              value={emailDomainFilter}
              onChange={(e) => setEmailDomainFilter(e.target.value)}
              className="w-48"
            />
            <Input
              placeholder="Filter by campaign..."
              value={campaignNameFilter}
              onChange={(e) => setCampaignNameFilter(e.target.value)}
              className="w-48"
            />
          </div>

          {loading ? (
            <div className="space-y-2">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : filteredMeetings.length === 0 ? (
            <Alert>
              <AlertDescription>
                No meetings found for the selected filters and time period.
              </AlertDescription>
            </Alert>
          ) : (
            <>
              {/* Bulk Actions Toolbar */}
              {selectedMeetings.size > 0 && (
                <div className="flex items-center justify-between p-4 bg-muted rounded-lg mb-4">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">
                      {selectedMeetings.size} meeting{selectedMeetings.size !== 1 ? 's' : ''} selected
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setIsBulkEditOpen(true)}
                      className="gap-2"
                    >
                      <Edit className="w-4 h-4" />
                      Bulk Edit
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleBulkPromoteToQualified}
                      className="gap-2"
                      disabled={Array.from(selectedMeetings).every(id => {
                        const meeting = filteredMeetings.find(m => m.id === id)
                        return meeting?.meeting_status === 'Qualified Meeting'
                      })}
                    >
                      <UserCheck className="w-4 h-4" />
                      Promote to Qualified
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleBulkDelete}
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Delete Selected
                    </Button>
                  </div>
                </div>
              )}
              
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">
                        <Checkbox
                          checked={isAllSelected}
                          onCheckedChange={handleSelectAll}
                          aria-label="Select all"
                        />
                      </TableHead>
                      {columnConfigs
                        .filter(col => col.visible)
                        .sort((a, b) => a.order - b.order)
                        .map(col => (
                          <TableHead key={col.id}>{col.label}</TableHead>
                        ))}
                      <TableHead className="w-24">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {/* Meetings Booked Section */}
                    {filteredMeetings.filter(m => m.meeting_status === 'Meeting Booked').length > 0 && (
                      <>
                        <TableRow className="bg-blue-50" data-section="meetings-booked">
                          <TableCell colSpan={columnConfigs.filter(col => col.visible).length + 1} className="font-semibold text-blue-800 py-3">
                            📅 Meetings Booked ({filteredMeetings.filter(m => m.meeting_status === 'Meeting Booked').length})
                          </TableCell>
                        </TableRow>
                        {filteredMeetings.filter(m => m.meeting_status === 'Meeting Booked').map((meeting) => (
                      <TableRow 
                        key={meeting.id} 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => {
                          console.log('Row clicked for meeting:', meeting.lead_email)
                          handleViewMeeting(meeting)
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedMeetings.has(meeting.id)}
                            onCheckedChange={(checked) => handleSelectMeeting(meeting.id, !!checked)}
                            aria-label={`Select meeting for ${meeting.lead_email}`}
                          />
                        </TableCell>
                      {columnConfigs
                        .filter(col => col.visible)
                        .sort((a, b) => a.order - b.order)
                        .map(col => (
                           <TableCell key={col.id} className={col.id === 'campaign_name' ? 'max-w-48' : ''}>
                             {col.id === 'meetings_booked_create_date' && meeting.meetings_booked_create_date
                               ? format(new Date(meeting.meetings_booked_create_date), 'MMM dd, yyyy')
                               : col.id === 'meeting_month' && meeting.meetings_booked_create_date
                               ? format(new Date(meeting.meetings_booked_create_date), 'MMM yyyy')
                               : col.id === 'meeting_status'
                               ? <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${
                                   meeting.meeting_status === 'Qualified Meeting' 
                                     ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                                     : 'bg-blue-100 text-blue-800 border border-blue-200'
                                 }`}>
                                   <div className={`w-2 h-2 rounded-full ${
                                     meeting.meeting_status === 'Qualified Meeting' ? 'bg-emerald-500' : 'bg-blue-500'
                                   }`}></div>
                                   {meeting.meeting_status}
                                 </div>
                                : col.id === 'is_meeting_booked'
                                ? <div className="flex items-center gap-2">
                                    <Checkbox 
                                      checked={Boolean(meeting.is_meeting_booked)}
                                       onCheckedChange={async (checked) => {
                                         console.log('Meeting Booked checkbox clicked:', { 
                                           meetingId: meeting.id, 
                                           checked, 
                                           currentState: meeting.is_meeting_booked, 
                                           tableSource: meeting.table_source 
                                         })
                                         const now = new Date().toISOString()
                                         
                                         try {
                                           // Determine which table to update based on table_source
                                           const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                           console.log('Updating table:', tableName)
                                           
                                           const { data, error } = await supabase
                                             .from(tableName)
                                             .update({ 
                                               is_meeting_booked: Boolean(checked),
                                               meeting_booked_date: checked ? now : null
                                             })
                                             .eq('id', meeting.id)
                                             .select('id, is_meeting_booked, meeting_booked_date')
                                           
                                           if (error) {
                                             console.error('Error updating meeting booked status:', error)
                                           } else {
                                             console.log('Successfully updated meeting booked status:', data)
                                             // Update local state immediately
                                             setRawMeetings(prev => prev.map(m => 
                                               m.id === meeting.id ? { 
                                                 ...m, 
                                                 is_meeting_booked: Boolean(checked),
                                                 meeting_booked_date: checked ? now : null
                                               } : m
                                             ))
                                           }
                                         } catch (err) {
                                           console.error('Exception updating meeting booked status:', err)
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {meeting.meeting_booked_date && (
                                      <span className="text-xs text-muted-foreground">
                                        {format(new Date(meeting.meeting_booked_date), 'MMM dd')}
                                      </span>
                                    )}
                                  </div>
                                : col.id === 'is_qualified_meeting'
                                ? <div className="flex items-center gap-2">
                                    <Checkbox 
                                      checked={Boolean(meeting.is_qualified_meeting)}
                                      onCheckedChange={async (checked) => {
                                        console.log('Qualified Meeting checkbox clicked:', { 
                                          meetingId: meeting.id, 
                                          checked, 
                                          currentState: meeting.is_qualified_meeting 
                                        })
                                        const now = new Date().toISOString()
                                        
                                        try {
                                           // Determine which table to update based on table_source
                                           const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                           console.log('Updating qualified table:', tableName)
                                           
                                           const { data, error } = await supabase
                                             .from(tableName)
                                             .update({ 
                                               is_qualified_meeting: Boolean(checked),
                                               qualified_meeting_date: checked ? now : null
                                             })
                                             .eq('id', meeting.id)
                                             .select('id, is_qualified_meeting, qualified_meeting_date')
                                          
                                          if (error) {
                                            console.error('Error updating qualified meeting status:', error)
                                          } else {
                                            console.log('Successfully updated qualified meeting status:', data)
                                            // Update local state immediately
                                            setRawMeetings(prev => prev.map(m => 
                                              m.id === meeting.id ? { 
                                                ...m, 
                                                is_qualified_meeting: Boolean(checked),
                                                qualified_meeting_date: checked ? now : null
                                              } : m
                                            ))
                                          }
                                        } catch (err) {
                                          console.error('Exception updating qualified meeting status:', err)
                                        }
                                      }}
                                      onClick={(e) => e.stopPropagation()}
                                    />
                                    {meeting.qualified_meeting_date && (
                                      <span className="text-xs text-muted-foreground">
                                        {format(new Date(meeting.qualified_meeting_date), 'MMM dd')}
                                      </span>
                                    )}
                                  </div>
                                : col.id === 'client'
                                ? <div onClick={(e) => e.stopPropagation()}>
                                    <EditableDropdown
                                      value={meeting.client}
                                      options={clients.map(c => ({ value: c.name, label: c.name }))}
                                      onSave={async (value) => {
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'client', value)
                                        if (success) {
                                          // Update local state
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, client: value } : m
                                          ))
                                        }
                                      }}
                                      placeholder="Select client"
                                      allowCreate={true}
                                      className="min-w-[120px]"
                                    />
                                  </div>
                                : col.id === 'lead_status'
                                ? <div onClick={(e) => e.stopPropagation()}>
                                    <InlineEdit
                                      value={meeting.lead_status || ''}
                                      onSave={async (value) => {
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'lead_status', value)
                                        if (success) {
                                          // Update local state
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, lead_status: value } : m
                                          ))
                                        }
                                      }}
                                      placeholder="Click to edit"
                                      className="min-w-[100px]"
                                    />
                                  </div>
                                : col.id === 'direct_meeting'
                                ? <div onClick={(e) => e.stopPropagation()}>
                                    <EditableDropdown
                                      value={meeting.direct_meeting ? 'true' : 'false'}
                                      options={[
                                        { value: 'true', label: 'Direct Meeting' },
                                        { value: 'false', label: 'Attribution Meeting' }
                                      ]}
                                      onSave={async (value) => {
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'direct_meeting', value === 'true')
                                        if (success) {
                                          // Update local state
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, direct_meeting: value === 'true' } : m
                                          ))
                                        }
                                      }}
                                      placeholder="Select type"
                                      className="min-w-[140px]"
                                    />
                                  </div>
                                : col.id === 'meeting_tier'
                                ? <div onClick={(e) => e.stopPropagation()}>
                                    <EditableDropdown
                                      value={meeting.meeting_tier || 'Tier 1'}
                                      options={[
                                        { value: 'Tier 1', label: 'Tier 1' },
                                        { value: 'Tier 2', label: 'Tier 2' },
                                        { value: 'Tier 3', label: 'Tier 3' },
                                        { value: 'Tier 4', label: 'Tier 4' },
                                        { value: 'Individual', label: 'Individual' }
                                      ]}
                                      onSave={async (value) => {
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'meeting_tier', value)
                                        if (success) {
                                          // Update local state
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, meeting_tier: value } : m
                                          ))
                                        }
                                      }}
                                      placeholder="Select tier"
                                      className="min-w-[100px]"
                                    />
                                  </div>
                                : col.id === 'email_sent_time' && meeting.email_sent_time
                                ? format(new Date(meeting.email_sent_time), 'MMM dd, yyyy HH:mm')
                                : col.id === 'op_created_date' && meeting.op_created_date
                                ? format(new Date(meeting.op_created_date), 'MMM dd, yyyy HH:mm')
                                : col.id === 'attribution_days'
                                ? `${meeting.attribution_days || 0} days`
                                : col.id === 'source'
                                ? meeting.source === 'breakcold' ? 'Direct' : meeting.source === 'sqls_meetings_booked' ? 'Attribution' : meeting.source
                                : col.id === 'campaign_name'
                                ? <div className="truncate max-w-48" title={meeting.campaign_name || 'N/A'}>{meeting.campaign_name || 'N/A'}</div>
                                : meeting[col.id as keyof MeetingRecord] || 'N/A'}
                           </TableCell>
                        ))}
                      <TableCell>
                        <div 
                          className="flex items-center gap-1"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {meeting.meeting_status === 'Meeting Booked' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePromoteToQualified(meeting)}
                              title="Promote to Qualified Meeting"
                              className="text-green-600 hover:text-green-700"
                            >
                              <UserCheck className="w-3 h-3" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEditMeeting(meeting)}
                          >
                            <Edit className="w-3 h-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                        ))}
                      </>
                    )}
                    
                    {/* Qualified Meetings Section */}
                    {filteredMeetings.filter(m => m.meeting_status === 'Qualified Meeting').length > 0 && (
                      <>
                        <TableRow className="bg-emerald-50" data-section="qualified-meetings">
                          <TableCell colSpan={columnConfigs.filter(col => col.visible).length + 1} className="font-semibold text-emerald-800 py-3">
                            ✅ Qualified Meetings ({filteredMeetings.filter(m => m.meeting_status === 'Qualified Meeting').length})
                          </TableCell>
                        </TableRow>
                        {filteredMeetings.filter(m => m.meeting_status === 'Qualified Meeting').map((meeting) => (
                      <TableRow 
                        key={meeting.id} 
                        className="cursor-pointer hover:bg-muted/50 transition-colors"
                        onClick={(e) => {
                          console.log('Row clicked for meeting:', meeting.lead_email)
                          handleViewMeeting(meeting)
                        }}
                      >
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Checkbox
                            checked={selectedMeetings.has(meeting.id)}
                            onCheckedChange={(checked) => handleSelectMeeting(meeting.id, !!checked)}
                            aria-label={`Select meeting for ${meeting.lead_email}`}
                          />
                        </TableCell>
                      {columnConfigs
                        .filter(col => col.visible)
                        .sort((a, b) => a.order - b.order)
                        .map(col => (
                           <TableCell key={col.id} className={col.id === 'campaign_name' ? 'max-w-48' : ''}>
                             {col.id === 'meetings_booked_create_date' && meeting.meetings_booked_create_date
                               ? format(new Date(meeting.meetings_booked_create_date), 'MMM dd, yyyy')
                               : col.id === 'meeting_month' && meeting.meetings_booked_create_date
                               ? format(new Date(meeting.meetings_booked_create_date), 'MMM yyyy')
                               : col.id === 'meeting_status'
                               ? <div className={`px-3 py-1 rounded-full text-xs font-medium flex items-center gap-2 ${
                                   meeting.meeting_status === 'Qualified Meeting' 
                                     ? 'bg-emerald-100 text-emerald-800 border border-emerald-200' 
                                     : 'bg-blue-100 text-blue-800 border border-blue-200'
                                 }`}>
                                   <div className={`w-2 h-2 rounded-full ${
                                     meeting.meeting_status === 'Qualified Meeting' ? 'bg-emerald-500' : 'bg-blue-500'
                                   }`}></div>
                                   {meeting.meeting_status}
                                 </div>
                                : col.id === 'is_meeting_booked'
                                ? <div className="flex items-center gap-2">
                                    <Checkbox 
                                      checked={Boolean(meeting.is_meeting_booked)}
                                       onCheckedChange={async (checked) => {
                                         console.log('Meeting Booked checkbox clicked:', { 
                                           meetingId: meeting.id, 
                                           checked, 
                                           currentState: meeting.is_meeting_booked, 
                                           tableSource: meeting.table_source 
                                         })
                                         
                                         const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                         const success = await updateMeetingField(meeting.id, tableName, 'is_meeting_booked', checked)
                                         if (success) {
                                           setRawMeetings(prev => prev.map(m => 
                                             m.id === meeting.id ? { ...m, is_meeting_booked: Boolean(checked) } : m
                                           ))
                                         }
                                       }}
                                    />
                                  </div>
                                : col.id === 'is_qualified_meeting'
                                ? <div className="flex items-center gap-2">
                                    <Checkbox 
                                      checked={Boolean(meeting.is_qualified_meeting)}
                                      onCheckedChange={async (checked) => {
                                        console.log('Qualified Meeting checkbox clicked:', { 
                                          meetingId: meeting.id, 
                                          checked, 
                                          currentState: meeting.is_qualified_meeting, 
                                          tableSource: meeting.table_source 
                                        })
                                        
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'is_qualified_meeting', checked)
                                        if (success) {
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, is_qualified_meeting: Boolean(checked) } : m
                                          ))
                                        }
                                      }}
                                    />
                                  </div>
                                : col.id === 'client'
                                ? <div className="flex items-center gap-2">
                                    <Building2 className="w-3 h-3 text-muted-foreground" />
                                    <span className="font-medium">{meeting.client}</span>
                                  </div>
                                : col.id === 'lead_email'
                                ? <div className="flex items-center gap-2">
                                    <Mail className="w-3 h-3 text-muted-foreground" />
                                    <span className="font-medium">{meeting.lead_email}</span>
                                  </div>
                                : col.id === 'email_domain'
                                ? <div className="flex items-center gap-2">
                                    <Globe className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-sm text-muted-foreground">{meeting.email_domain}</span>
                                  </div>
                                : col.id === 'campaign_name'
                                ? <div className="flex items-center gap-2">
                                    <Target className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-sm font-medium truncate">{meeting.campaign_name}</span>
                                  </div>
                                : col.id === 'meeting_tier'
                                ? <div className="flex items-center gap-2">
                                    <Crown className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-sm font-medium">{meeting.meeting_tier || 'Tier 1'}</span>
                                  </div>
                                : col.id === 'source'
                                ? <div className="flex items-center gap-2">
                                    <Database className="w-3 h-3 text-muted-foreground" />
                                    <span className="text-sm font-medium">{meeting.source}</span>
                                  </div>
                                : col.id === 'direct_meeting'
                                ? <div onClick={(e) => e.stopPropagation()}>
                                    <EditableDropdown
                                      value={meeting.direct_meeting ? 'true' : 'false'}
                                      options={[
                                        { value: 'true', label: 'Direct Meeting' },
                                        { value: 'false', label: 'Attribution Meeting' }
                                      ]}
                                      onSave={async (value) => {
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'direct_meeting', value === 'true')
                                        if (success) {
                                          // Update local state
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, direct_meeting: value === 'true' } : m
                                          ))
                                        }
                                      }}
                                      placeholder="Select type"
                                      className="min-w-[140px]"
                                    />
                                  </div>
                                : col.id === 'meeting_tier'
                                ? <div onClick={(e) => e.stopPropagation()}>
                                    <EditableDropdown
                                      value={meeting.meeting_tier || 'Tier 1'}
                                      options={[
                                        { value: 'Tier 1', label: 'Tier 1' },
                                        { value: 'Tier 2', label: 'Tier 2' },
                                        { value: 'Tier 3', label: 'Tier 3' },
                                        { value: 'Tier 4', label: 'Tier 4' },
                                        { value: 'Individual', label: 'Individual' }
                                      ]}
                                      onSave={async (value) => {
                                        const tableName = meeting.table_source === 'qualified_meetings' ? 'qualified_meetings' : 'meetings_booked'
                                        const success = await updateMeetingField(meeting.id, tableName, 'meeting_tier', value)
                                        if (success) {
                                          setRawMeetings(prev => prev.map(m => 
                                            m.id === meeting.id ? { ...m, meeting_tier: value } : m
                                          ))
                                        }
                                      }}
                                      placeholder="Select tier"
                                      className="min-w-[100px]"
                                    />
                                  </div>
                                : meeting[col.id as keyof typeof meeting]
                            }
                          </TableCell>
                        ))}
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <div className="flex items-center gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleViewMeeting(meeting)}
                            >
                              <Eye className="w-3 h-3" />
                            </Button>
                            {/* Qualify button removed - not implemented yet */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditMeeting(meeting)}
                            >
                              <Edit className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                        ))}
                      </>
                    )}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Enhanced Meeting Detail Dialog */}
      {isDetailViewOpen && selectedMeeting && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-4xl w-full max-h-[90vh] overflow-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold">Meeting Details</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsDetailViewOpen(false)}
                  className="gap-2"
                >
                  ✕ Close
                </Button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Lead Email</label>
                    <p className="text-lg">{selectedMeeting.lead_email}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Client</label>
                    <p className="text-lg">{selectedMeeting.client}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Meeting Status</label>
                    <div className={`inline-flex px-3 py-1 rounded-full text-sm font-medium ${
                      selectedMeeting.meeting_status === 'Qualified Meeting' 
                        ? 'bg-emerald-100 text-emerald-800' 
                        : 'bg-blue-100 text-blue-800'
                    }`}>
                      {selectedMeeting.meeting_status}
                    </div>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Email Domain</label>
                    <p className="text-lg">{selectedMeeting.email_domain}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Campaign Name</label>
                    <p className="text-lg">{selectedMeeting.campaign_name || 'N/A'}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Meeting Date</label>
                    <p className="text-lg">
                      {selectedMeeting.meetings_booked_create_date 
                        ? format(new Date(selectedMeeting.meetings_booked_create_date), 'PPP') 
                        : 'N/A'}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Source</label>
                    <p className="text-lg">
                      {selectedMeeting.source === 'breakcold' ? 'Direct' : 
                       selectedMeeting.source === 'sqls_meetings_booked' ? 'Attribution' : 
                       selectedMeeting.source}
                    </p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Direct Meeting</label>
                    <p className="text-lg">{selectedMeeting.direct_meeting ? 'Yes' : 'No'}</p>
                  </div>
                  
                  <div>
                    <label className="text-sm font-medium text-muted-foreground">Attribution Days</label>
                    <p className="text-lg">{selectedMeeting.attribution_days || 0} days</p>
                  </div>
                  
                  {selectedMeeting.email_sent_time && (
                    <div>
                      <label className="text-sm font-medium text-muted-foreground">Email Sent Time</label>
                      <p className="text-lg">
                        {format(new Date(selectedMeeting.email_sent_time), 'PPP p')}
                      </p>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between mt-8 pt-6 border-t">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setIsDetailViewOpen(false)
                      handleEditMeeting(selectedMeeting)
                    }}
                    className="gap-2"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Meeting
                  </Button>
                  {selectedMeeting.meeting_status === 'Meeting Booked' && (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setIsDetailViewOpen(false)
                        handlePromoteToQualified(selectedMeeting)
                      }}
                      className="gap-2"
                    >
                      <UserCheck className="w-4 h-4" />
                      Promote to Qualified
                    </Button>
                  )}
                </div>
                <Button
                  variant="destructive"
                  onClick={() => {
                    setIsDetailViewOpen(false)
                    handleDeleteMeeting()
                  }}
                  className="gap-2"
                >
                  <Trash2 className="w-4 h-4" />
                  Delete Meeting
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Edit Dialog */}
      {isBulkEditOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold">Bulk Edit Meetings</h2>
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={() => setIsBulkEditOpen(false)}
                >
                  ✕
                </Button>
              </div>
              
              <p className="text-muted-foreground mb-4">
                Editing {selectedMeetings.size} selected meeting{selectedMeetings.size !== 1 ? 's' : ''}
              </p>
              
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium">Change Status</label>
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        handleBulkPromoteToQualified()
                        setIsBulkEditOpen(false)
                      }}
                      className="gap-2"
                    >
                      <UserCheck className="w-4 h-4" />
                      Promote to Qualified
                    </Button>
                  </div>
                </div>
                
                <div className="pt-4 border-t">
                  <Button
                    variant="destructive"
                    onClick={() => {
                      handleBulkDelete()
                      setIsBulkEditOpen(false)
                    }}
                    className="w-full gap-2"
                  >
                    <Trash2 className="w-4 h-4" />
                    Delete All Selected
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manual Meeting Dialogs */}
      <ManualMeetingDialog
        isOpen={isAddMeetingOpen}
        onClose={() => setIsAddMeetingOpen(false)}
        onSuccess={handleMeetingSaved}
        existingMeeting={null}
      />

      <ManualMeetingDialog
        isOpen={isEditMeetingOpen}
        onClose={() => setIsEditMeetingOpen(false)}
        onSuccess={handleMeetingSaved}
        existingMeeting={selectedMeeting}
      />

      <MeetingDetailView
        isOpen={isDetailViewOpen}
        onClose={() => setIsDetailViewOpen(false)}
        onEdit={() => {
          console.log('Opening edit dialog for meeting:', selectedMeeting?.id)
          setIsDetailViewOpen(false)
          setIsEditMeetingOpen(true)
        }}
        onDelete={handleDeleteMeeting}
        meeting={selectedMeeting}
      />
    </div>
  )
}